// Release detail — the primary authoring surface. Editors edit here.
// Viewers see the read-only Timeline view instead.
//
// Layout:
//   header       — tag, repo, publish state, publish/unpublish/delete
//   editor form  — title, narrative (with Regenerate), release_date_override
//   media        — upload + list
//   items        — Regenerate-items button, add item, per-item ItemEditor
//   github_body  — folded raw markdown as a fallback reference
import { Link, useLocation, useRoute } from "wouter";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type {
  ItemCategory,
  Release,
  ReleaseItem,
  UnifiedRelease,
} from "@release-hub/shared";
import { useAllReleases, useRelease, useReleases } from "@/api/queries";
import {
  useAddBuilds,
  useAddItem,
  useDeleteRelease,
  useMergeRelease,
  usePatchRelease,
  usePublishRelease,
  useSplitRelease,
} from "@/api/mutations";
import { ApiError } from "@/api/client";
import { useEffectiveRole } from "@/auth/role";
import { AutofillPanel } from "@/components/AutofillPanel";
import { AutoTextarea } from "@/components/AutoTextarea";
import { FlagPicker } from "@/components/FlagPicker";
import { HistoryDrawer } from "@/components/HistoryDrawer";
import { InfoTip } from "@/components/InfoTip";
import { ItemEditor } from "@/components/ItemEditor";
import { LoadingLine } from "@/components/LoadingLine";
import { MediaUpload } from "@/components/MediaUpload";
import { PromptsModal } from "@/components/PromptsModal";
import { SeedingSkeleton } from "@/components/SeedingSkeleton";
import { Button } from "@/ui/button";
import { Loading, Spinner } from "@/ui/spinner";
import { useConfirm } from "@/ui/confirm";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/dialog";
import { fmtDate, fmtDateTime } from "@/lib/format";
import { releaseUrl } from "@/lib/url";
import { cn } from "@/lib/cn";
import { CopyLinkButton } from "@/components/CopyLinkButton";

export function ReleaseDetail() {
  const [, params] = useRoute<{ id: string }>("/releases/:id");
  const id = params?.id;
  const [, navigate] = useLocation();
  const { data, isPending, error } = useRelease(id);
  const { isEditor, canEditContent } = useEffectiveRole();
  // Escape hatch if the background seed hangs (see SeedingSkeleton).
  const [bypassSeed, setBypassSeed] = useState(false);

  if (isPending) return <LoadingLine />;
  if (error) {
    return (
      <div className="space-y-4 py-6">
        <BackLink />
        <p className="text-destructive">
          Failed to load: {error instanceof Error ? error.message : "unknown"}
        </p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="space-y-4 py-6">
        <BackLink />
        <p className="text-text-tertiary">Release "{id}" not found.</p>
      </div>
    );
  }
  // Freshly converted — the LLM is drafting the narrative + items in the
  // background. Show the seeding skeleton (and block editing) until it clears.
  if (data.seeding_at && !bypassSeed && (isEditor || canEditContent)) {
    return (
      <SeedingSkeleton
        message="Generating the narrative and items from the release notes…"
        onBypass={() => setBypassSeed(true)}
      />
    );
  }

  // Viewers land here for a published release — read-only Timeline-shaped view.
  // Content Authors (PMM) get the editor capped to copy (title / narrative /
  // media); editors get the full authoring surface.
  if (!isEditor && !canEditContent) return <ReadOnlyView release={data} />;

  return (
    <EditorView
      release={data}
      contentOnly={!isEditor}
      onDeleted={() => navigate("/timeline")}
    />
  );
}

function BackLink() {
  return (
    <Link
      href="/timeline"
      className="inline-flex items-center gap-1 text-sm text-text-secondary no-underline hover:text-text"
    >
      ← Timeline
    </Link>
  );
}

function ReadOnlyView({ release }: { release: Release }) {
  const primary = release.builds?.[0];
  // Prefer an editor-set title; fall back to the version tag (the default
  // customers see when no title is set).
  const title = release.title?.trim() || primary?.tag_name || "Untitled";
  return (
    <div className="space-y-4 py-6">
      <BackLink />
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-serif text-3xl">{title}</h1>
          <p className="mt-1 text-sm text-text-secondary">
            {primary?.repo ?? ""} ·{" "}
            {release.published_at
              ? fmtDate(release.published_at)
              : "not published"}
          </p>
        </div>
        <CopyLinkButton
          url={releaseUrl(release.id)}
          className="inline-flex items-center gap-1.5 rounded-md border border-border-hover bg-card px-2.5 py-1 text-xs text-text-secondary hover:border-text/40 hover:text-text"
        />
      </header>
      {release.narrative && (
        <p className="text-lg leading-relaxed">{release.narrative}</p>
      )}
      <p className="text-text-tertiary">
        Item view coming with the Timeline — this page is the editor.
      </p>
    </div>
  );
}

function EditorView({
  release,
  onDeleted,
  contentOnly = false,
}: {
  release: Release;
  onDeleted: () => void;
  /** Content Author (PMM) mode: only title / narrative / media are editable.
   *  Items are read-only, and publish / delete / generate / advanced settings
   *  / history are hidden — matching the server-side PMM whitelist. */
  contentOnly?: boolean;
}) {
  const patch = usePatchRelease();
  const publish = usePublishRelease();
  const del = useDeleteRelease();
  const split = useSplitRelease();
  const confirm = useConfirm();
  const [deleteErr, setDeleteErr] = useState<string>("");
  const [splitErr, setSplitErr] = useState<string>("");
  const [promptsOpen, setPromptsOpen] = useState(false);
  const [autofillOpen, setAutofillOpen] = useState(false);
  const [addBuildsOpen, setAddBuildsOpen] = useState(false);
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  // Planned release: scheduled before any GitHub build exists. Target date +
  // build cut are the headline controls; attach the real build via +Add build.
  const isPlanned =
    (release.builds?.length ?? 0) === 0 && !!release.planned_surface;
  const isMobileRelease =
    release.planned_surface === "mobile" ||
    (release.builds ?? []).some((b) => b.surface === "mobile");

  const [title, setTitle] = useState(release.title ?? "");
  const [narrative, setNarrative] = useState(release.narrative ?? "");
  const [dateOverride, setDateOverride] = useState(
    release.release_date_override ? release.release_date_override.slice(0, 16) : "",
  );
  // Build-cut day (YYYY-MM-DD, mobile only). "" = none. Mirrors to the
  // release calendar as its own "Build Cut EOD" event on save.
  const [buildCut, setBuildCut] = useState(release.build_cut_date ?? "");
  // Timeline prominence override. "" = auto (infer from item categories).
  const [prominence, setProminence] = useState<"" | "minor" | "normal">(
    release.visual_prominence ?? "",
  );
  const [titleErr, setTitleErr] = useState<string>("");
  const [narrErr, setNarrErr] = useState<string>("");
  const [settingsErr, setSettingsErr] = useState<string>("");
  const [publishErr, setPublishErr] = useState<string>("");

  // Reset local state when the server sends a fresh copy (e.g., after a
  // Generate apply). Ignore during pending mutations so we don't clobber typing.
  useEffect(() => {
    if (patch.isPending) return;
    setTitle(release.title ?? "");
    setNarrative(release.narrative ?? "");
    setDateOverride(
      release.release_date_override
        ? release.release_date_override.slice(0, 16)
        : "",
    );
    setProminence(release.visual_prominence ?? "");
    setBuildCut(release.build_cut_date ?? "");
  }, [
    release.title,
    release.narrative,
    release.release_date_override,
    release.visual_prominence,
    release.build_cut_date,
    patch.isPending,
  ]);

  const titleDirty = title !== (release.title ?? "");
  const narrDirty = narrative !== (release.narrative ?? "");
  const settingsDirty =
    dateOverride !==
      (release.release_date_override
        ? release.release_date_override.slice(0, 16)
        : "") ||
    prominence !== (release.visual_prominence ?? "") ||
    buildCut !== (release.build_cut_date ?? "");

  const saveTitle = async () => {
    setTitleErr("");
    try {
      await patch.mutateAsync({
        id: release.id,
        patch: { title: title.trim() || null },
      });
    } catch (e) {
      setTitleErr(errMsg(e));
    }
  };

  const saveNarrative = async () => {
    setNarrErr("");
    try {
      await patch.mutateAsync({
        id: release.id,
        patch: { narrative: narrative.trim() || null },
      });
    } catch (e) {
      setNarrErr(errMsg(e));
    }
  };

  const saveSettings = async () => {
    setSettingsErr("");
    try {
      await patch.mutateAsync({
        id: release.id,
        patch: {
          release_date_override: dateOverride || null,
          visual_prominence: prominence === "" ? null : prominence,
          build_cut_date: buildCut || null,
        },
      });
    } catch (e) {
      setSettingsErr(errMsg(e));
    }
  };

  const doMedia = (urls: string[], captions: Record<string, string>) =>
    patch.mutate({ id: release.id, patch: { media_urls: urls, media_captions: captions } });

  const publishIt = async () => {
    setPublishErr("");
    try {
      await publish.mutateAsync({
        id: release.id,
        publish: release.status !== "published",
      });
    } catch (e) {
      if (e instanceof ApiError && (e.body as { missing?: string[] })?.missing) {
        setPublishErr(
          `Missing: ${(e.body as { missing: string[] }).missing.join(", ")}`,
        );
      } else {
        setPublishErr(errMsg(e));
      }
    }
  };

  const deleteIt = async () => {
    setDeleteErr("");
    const ok = await confirm({
      title: "Delete this release?",
      description:
        "Removes the curated release and all its items. The raw GitHub row stays, so you can regenerate the notes later.",
      confirmLabel: "Delete",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      await del.mutateAsync(release.id);
      onDeleted();
    } catch (e) {
      setDeleteErr(errMsg(e));
    }
  };

  const splitIt = async () => {
    setSplitErr("");
    const buildCount = release.builds?.length ?? 0;
    const ok = await confirm({
      title: "Split into separate releases?",
      description: (
        <>
          Turns this release into <strong>{buildCount}</strong> independent
          releases — one per platform. Title, narrative, and all items are
          cloned into each. Edits after split are independent; this can't be
          undone here (though you could delete a clone if needed).
        </>
      ),
      confirmLabel: "Split",
    });
    if (!ok) return;
    try {
      await split.mutateAsync(release.id);
      // Stay on the original release — it now covers just the first build.
      // The clones show up on the Releases list under their own release_ids.
    } catch (e) {
      setSplitErr(errMsg(e));
    }
  };

  const items = release.items ?? [];

  // Pre-fill "Autofill from notes" with the release's source text: the
  // hand-authored source-body override if set, else the concatenated GitHub
  // bodies of the wrapped builds. The editor can trim or wipe it before
  // generating — this replaces the old "Regenerate from GitHub" button.
  const releaseSourceBlurb = (
    release.source_body_override?.trim() ||
    (release.builds ?? [])
      .map((b) => b.body ?? "")
      .filter((s) => s.trim())
      .join("\n\n---\n\n")
  ).trim();

  return (
    <div className="animate-in fade-in-0 space-y-6 py-6 duration-300">
      <BackLink />

      <AutofillPanel
        kind="release"
        entityId={release.id}
        current={release}
        open={autofillOpen}
        onOpenChange={setAutofillOpen}
        defaultBlurb={releaseSourceBlurb}
      />

      {/* Header */}
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2 text-sm text-text-secondary">
            {(release.builds ?? []).map((b) => {
              const label =
                b.surface === "web"
                  ? "Web"
                  : b.platform === "ios"
                    ? "iOS"
                    : b.platform === "android"
                      ? "Android"
                      : "Mobile";
              return (
                <span
                  key={b.github_release_id}
                  className={
                    b.surface === "web"
                      ? "inline-flex items-center gap-1.5 rounded-full bg-nightly-soft px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-nightly"
                      : "inline-flex items-center gap-1.5 rounded-full bg-mobile-soft px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-mobile"
                  }
                >
                  {label}
                  <span className="font-mono normal-case tracking-normal opacity-70">
                    {b.tag_name}
                  </span>
                  {b.html_url && (
                    <a
                      href={b.html_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="normal-case tracking-normal opacity-70 hover:opacity-100"
                    >
                      ↗
                    </a>
                  )}
                </span>
              );
            })}
            {isPlanned && (
              <span
                className={
                  release.planned_surface === "web"
                    ? "inline-flex items-center gap-1.5 rounded-full bg-nightly-soft px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-nightly"
                    : "inline-flex items-center gap-1.5 rounded-full bg-mobile-soft px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-mobile"
                }
              >
                {release.planned_surface === "web" ? "Web" : "Mobile"}
                <span className="normal-case tracking-normal opacity-70">planned</span>
              </span>
            )}
            <span
              className={
                release.status === "published"
                  ? "rounded-full bg-ga-soft px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-ga"
                  : "rounded-full bg-border px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-text-secondary"
              }
            >
              {release.status}
            </span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-2">
            <CopyLinkButton
              url={releaseUrl(release.id)}
              className="inline-flex items-center gap-1.5 rounded-md border border-border-hover bg-card px-2.5 py-1 text-xs text-text-secondary hover:border-text/40 hover:text-text"
            />
            {!contentOnly && (
              <Button
                variant="outline"
                onClick={() => setAutofillOpen(true)}
                title="Paste notes (pre-filled with the GitHub body) and let AI draft the narrative + items"
              >
                Generate with AI
              </Button>
            )}
            {!contentOnly && (
              <Button
                variant={release.status === "published" ? "destructive" : "default"}
                onClick={publishIt}
                loading={publish.isPending}
              >
                {release.status === "published" ? "Unpublish" : "Publish"}
              </Button>
            )}
            {!contentOnly && (
            <MoreMenu>
              {(close) => (
                <>
                  <MenuItem
                    onClick={() => {
                      close();
                      setAddBuildsOpen(true);
                    }}
                  >
                    + Add build
                  </MenuItem>
                  {(release.builds?.length ?? 0) >= 2 && (
                    <MenuItem
                      onClick={() => {
                        close();
                        splitIt();
                      }}
                      disabled={split.isPending}
                    >
                      Split into per-platform releases
                    </MenuItem>
                  )}
                  <MergeControl survivor={release} onDone={close} />
                  <MenuItem
                    onClick={() => {
                      close();
                      setPromptsOpen(true);
                    }}
                  >
                    Edit AI prompts
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      close();
                      setHistoryOpen(true);
                    }}
                  >
                    Edit history
                  </MenuItem>
                  <div className="my-1 border-t border-border" />
                  <MenuItem
                    destructive
                    onClick={() => {
                      close();
                      deleteIt();
                    }}
                    disabled={del.isPending}
                  >
                    Delete release
                  </MenuItem>
                </>
              )}
            </MoreMenu>
            )}
          </div>
          {publishErr && (
            <span className="text-xs text-destructive">{publishErr}</span>
          )}
          {splitErr && (
            <span className="text-xs text-destructive">{splitErr}</span>
          )}
          {deleteErr && (
            <span className="text-xs text-destructive">{deleteErr}</span>
          )}
        </div>
      </header>

      {/* Planned release: no builds yet — target date + build cut are the
          headline controls (they mirror straight to the release calendar).
          Once a build is attached this section retires; the same fields keep
          living in Advanced. */}
      {isPlanned && !contentOnly && (
        <section className="space-y-4 rounded-lg border border-border bg-card p-5">
          <h3 className="flex items-center font-serif text-lg">
            Schedule
            <InfoTip>
              This release is <strong>planned</strong> — it exists ahead of any
              GitHub build. The target date and build cut sync to the shared
              release calendar. When the real release ships, use{" "}
              <strong>⋯ → + Add build</strong> to attach it.
            </InfoTip>
          </h3>
          <div className="flex flex-wrap gap-6">
            <Field label="Target date" info="The planned ship date. Drives the calendar event and the Timeline position.">
              {/* Day precision only. Stored at noon (matching the create
                  endpoint) so the calendar day can't slip across timezones. */}
              <input
                type="date"
                value={dateOverride.slice(0, 10)}
                onChange={(e) =>
                  setDateOverride(e.target.value ? `${e.target.value}T12:00` : "")
                }
                className={cn(inputCls, "max-w-[200px]")}
              />
            </Field>
            {isMobileRelease && (
              <Field
                label="Build cut"
                info="End-of-day deadline to cut the release branch. Syncs to the calendar as its own “Build Cut EOD” event. Clear it to remove the event."
                hint="Blank = no build-cut event"
              >
                <input
                  type="date"
                  value={buildCut}
                  onChange={(e) => setBuildCut(e.target.value)}
                  className={cn(inputCls, "max-w-[200px]")}
                />
              </Field>
            )}
          </div>
          <div className="flex items-center justify-end gap-3">
            {settingsErr && (
              <span className="text-sm text-destructive">{settingsErr}</span>
            )}
            <Button
              onClick={saveSettings}
              loading={patch.isPending && settingsDirty}
              disabled={!settingsDirty}
            >
              {patch.isPending && settingsDirty ? "Saving…" : "Save schedule"}
            </Button>
          </div>
        </section>
      )}

      {/* Background-seed indicator. Convert returns instantly and drafts the
          narrative + items via the LLM in the background; while that runs
          (useRelease polls on seeding_at) show a spinner so the empty
          narrative/items read as "generating", not "broken". */}
      {release.seeding_at && (
        <div className="flex items-center gap-2.5 rounded-lg border border-border bg-card/60 px-4 py-3 text-sm text-text-secondary">
          <Spinner size="sm" className="text-accent" />
          Generating narrative and items from the release notes… this fills in
          automatically.
        </div>
      )}

      {/* Title — optional human-readable name for the release. Blank falls
          back to the version tag, which is the default the card/Timeline
          show when no title is set. */}
      <section className="space-y-2 rounded-lg border border-border bg-card p-5">
        <Field
          label="Title"
          info="An optional headline for the release, shown on the release card and Timeline. Leave blank to fall back to the version tag (e.g. v3.4.0)."
        >
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={release.builds?.[0]?.tag_name ?? "Untitled"}
            className={inputCls}
          />
        </Field>
        <div className="flex items-center justify-end gap-3">
          {titleErr && <span className="text-sm text-destructive">{titleErr}</span>}
          <Button
            onClick={saveTitle}
            loading={patch.isPending && titleDirty}
            disabled={!titleDirty}
          >
            {patch.isPending && titleDirty ? "Saving…" : "Save"}
          </Button>
        </div>
      </section>

      {/* Narrative — the one- or two-sentence summary customers read at the
          top of the release card. */}
      <section className="space-y-2 rounded-lg border border-border bg-card p-5">
        <Field
          label="Narrative"
          info="One or two sentences summarizing what the release is about, in plain English. Shown at the top of the release card on the Timeline. Use Generate with AI to draft it from the GitHub body or pasted notes."
        >
          <AutoTextarea
            value={narrative}
            onChange={(e) => setNarrative(e.target.value)}
            className={cn(inputCls, "min-h-[9rem]")}
          />
        </Field>
        <div className="flex items-center justify-end gap-3">
          {narrErr && <span className="text-sm text-destructive">{narrErr}</span>}
          <Button
            onClick={saveNarrative}
            loading={patch.isPending && narrDirty}
            disabled={!narrDirty}
          >
            {patch.isPending && narrDirty ? "Saving…" : "Save"}
          </Button>
        </div>
      </section>

      {/* Media */}
      <section className="space-y-2 rounded-lg border border-border bg-card p-5">
        <div className="flex items-center justify-between">
          <h3 className="flex items-center font-serif text-lg">
            Media
            <InfoTip>
              Screenshots or short Looms shown on the release card. Great
              for visually-loaded changes (new UI, new dashboard, etc).
              Uploaded to release_hub storage; paste a URL or drag a file.
            </InfoTip>
          </h3>
        </div>
        <MediaUpload
          urls={release.media_urls ?? []}
          captions={release.media_captions ?? {}}
          onChange={doMedia}
        />
      </section>

      {/* Items — grouped by category; each row is read-mode and expands to
          the editor on click, so 100+ item releases stay scannable. */}
      <section className="space-y-3 rounded-lg border border-border bg-card p-5">
        <div className="flex items-center justify-between">
          <h3 className="flex items-center font-serif text-lg">
            Items
            <InfoTip>
              What shipped, split by category (feature, enhancement, bug fix,
              maintenance). Use <strong>Generate with AI</strong> to extract
              them from the GitHub body; + Add item drops a blank row. Click any
              row to edit it.
            </InfoTip>
          </h3>
          {!contentOnly && (
            <Button size="sm" onClick={() => setAddItemOpen(true)}>
              + Add item
            </Button>
          )}
        </div>
        {items.length === 0 ? (
          <p className="text-text-tertiary">
            {contentOnly
              ? "No items yet."
              : "No items yet. Add manually, or use Generate with AI to extract them from the GitHub body."}
          </p>
        ) : (
          <ItemGroups items={items} releaseId={release.id} readOnly={contentOnly} />
        )}
      </section>

      {/* Advanced — low-frequency display settings + raw source reference.
          Date override + prominence are curation controls, so editor-only. */}
      {!contentOnly && (
      <details className="rounded-lg border border-border bg-card p-5">
        <summary className="cursor-pointer font-serif text-lg">Advanced</summary>
        <div className="mt-4 space-y-4">
          {/* Planned releases edit their date in the Schedule section above —
              don't render the same state twice. */}
          {!isPlanned && (
            <Field
              label="Public date override"
              info="Set only if you want the Timeline to show a different date than GitHub's published_at (e.g. announcing a release a week after the tag went out). Otherwise leave blank."
              hint="Blank = use GitHub's published_at"
            >
              <input
                type="datetime-local"
                value={dateOverride}
                onChange={(e) => setDateOverride(e.target.value)}
                className={cn(inputCls, "max-w-[240px]")}
              />
            </Field>
          )}
          {!isPlanned && isMobileRelease && (
            <Field
              label="Build cut"
              info="End-of-day deadline to cut the release branch. Syncs to the shared release calendar as its own “Build Cut EOD” event. Clear it to remove the event."
              hint="Blank = no build-cut event"
            >
              <input
                type="date"
                value={buildCut}
                onChange={(e) => setBuildCut(e.target.value)}
                className={cn(inputCls, "max-w-[200px]")}
              />
            </Field>
          )}
          <Field
            label="Timeline prominence"
            info="Controls how big/muted this release looks on the Timeline. Auto infers 'minor' when every item is a bugfix/maintenance category — patch releases don't compete visually with feature launches. Force 'minor' to demote (e.g. an internal-only rev), or 'normal' to promote a bugfix release that shipped a notable behavior change."
          >
            <div className="inline-flex rounded-md border border-border-hover bg-bg p-0.5">
              {(
                [
                  ["", "Auto"],
                  ["normal", "Normal"],
                  ["minor", "Minor"],
                ] as ["" | "normal" | "minor", string][]
              ).map(([v, label]) => (
                <button
                  key={v || "auto"}
                  type="button"
                  onClick={() => setProminence(v)}
                  className={
                    prominence === v
                      ? "rounded-sm bg-card px-3 py-1 text-xs font-medium text-text shadow-sm"
                      : "rounded-sm px-3 py-1 text-xs text-text-secondary hover:text-text"
                  }
                >
                  {label}
                </button>
              ))}
            </div>
          </Field>
          <div className="flex items-center justify-end gap-3">
            {settingsErr && (
              <span className="text-sm text-destructive">{settingsErr}</span>
            )}
            <Button
              onClick={saveSettings}
              loading={patch.isPending && settingsDirty}
              disabled={!settingsDirty}
            >
              {patch.isPending && settingsDirty ? "Saving…" : "Save settings"}
            </Button>
          </div>

          {release.source_body_override && (
            <p className="border-t border-border pt-3 text-xs text-text-tertiary">
              A custom LLM source body is set for this release (managed via{" "}
              <strong>Generate with AI</strong> → “Save these notes as source”).
              Regenerations summarize it instead of the GitHub body below.
            </p>
          )}

          {/* Raw GitHub bodies — one per wrapped build, reference only. */}
          {(release.builds ?? [])
            .filter((b) => b.body)
            .map((b) => (
              <details
                key={b.github_release_id}
                className="rounded-md border border-border-hover bg-bg/40 p-3"
              >
                <summary className="cursor-pointer text-sm font-medium text-text-secondary">
                  GitHub release notes
                  {(release.builds?.length ?? 0) > 1 && (
                    <span className="ml-2 font-normal text-text-tertiary">
                      ({b.tag_name})
                    </span>
                  )}
                </summary>
                <pre className="mt-3 max-h-96 overflow-auto whitespace-pre-wrap rounded bg-bg p-3 font-mono text-xs">
                  {b.body}
                </pre>
              </details>
            ))}
        </div>
      </details>
      )}

      {release.last_edited_at && (
        <p className="text-xs text-text-tertiary">
          Last edited by{" "}
          <span className="font-medium text-text-secondary">
            {release.last_edited_by?.split("@")[0] ?? "someone"}
          </span>{" "}
          · <time>{fmtDateTime(release.last_edited_at)}</time>
          {!contentOnly && (
            <>
              {" "}
              ·{" "}
              <button
                type="button"
                onClick={() => setHistoryOpen(true)}
                className="underline decoration-dotted hover:text-text"
              >
                View history
              </button>
            </>
          )}
        </p>
      )}

      <PromptsModal open={promptsOpen} onOpenChange={setPromptsOpen} />
      <AddItemModal
        open={addItemOpen}
        onOpenChange={setAddItemOpen}
        releaseId={release.id}
      />
      <AddBuildsModal
        open={addBuildsOpen}
        onOpenChange={setAddBuildsOpen}
        release={release}
      />
      <HistoryDrawer
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        entityType="releases"
        entityId={release.id}
        title={release.builds?.[0]?.tag_name ?? release.title ?? release.id}
      />
    </div>
  );
}

/** Picker modal for the "+ Add build" action. Lists un-curated
 *  github_releases from the same repo(s) as the current release; editor
 *  checks one or more and confirms. Server auto-unignores ignored builds. */
function AddBuildsModal({
  open,
  onOpenChange,
  release,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  release: Release;
}) {
  const all = useAllReleases();
  const addBuilds = useAddBuilds();
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [err, setErr] = useState<string>("");

  // Reset selection whenever the modal opens.
  useEffect(() => {
    if (open) {
      setSelected(new Set());
      setErr("");
    }
  }, [open]);

  // Same-repo candidates only: web + mobile don't belong together.
  const repos = useMemo(
    () => new Set((release.builds ?? []).map((b) => b.repo)),
    [release.builds],
  );
  const candidates = useMemo(() => {
    const rows = all.data ?? [];
    return rows
      .filter((r): r is UnifiedRelease => r.release_id == null)
      .filter((r) => (repos.size === 0 ? true : repos.has(r.repo)))
      .sort((a, b) =>
        b.github_published_at.localeCompare(a.github_published_at),
      );
  }, [all.data, repos]);

  const toggle = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const submit = async () => {
    setErr("");
    if (selected.size === 0) return;
    try {
      await addBuilds.mutateAsync({
        id: release.id,
        ghIds: Array.from(selected),
      });
      onOpenChange(false);
    } catch (e) {
      setErr(errMsg(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Merge github releases into this one</DialogTitle>
          <DialogDescription>
            Pick one or more un-curated releases from the same repo. They'll
            be wrapped by this release. Ignored ones are auto-unignored on
            merge. Items and narrative aren't re-run automatically — use
            Regenerate after if you want the LLM to re-consider.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-96 overflow-auto rounded-md border border-border">
          {all.isPending ? (
            <Loading label="Loading builds" />
          ) : candidates.length === 0 ? (
            <div className="p-4 text-sm text-text-tertiary">
              No un-curated releases available in {Array.from(repos).join(", ") || "the matching repo"}.
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {candidates.map((c) => (
                <li key={c.github_release_id}>
                  <label className="flex cursor-pointer items-start gap-3 px-3 py-2 hover:bg-border/40">
                    <input
                      type="checkbox"
                      checked={selected.has(c.github_release_id)}
                      onChange={() => toggle(c.github_release_id)}
                      className="mt-1"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2 text-xs text-text-secondary">
                        <span className="font-mono">{c.tag_name}</span>
                        <span className="text-text-tertiary">·</span>
                        <span>{c.repo}</span>
                        <span className="text-text-tertiary">·</span>
                        <time>{fmtDate(c.github_published_at)}</time>
                        {c.ignored && (
                          <span className="rounded-full border border-border px-1.5 py-0.5 font-semibold uppercase tracking-wider text-text-tertiary">
                            ignored
                          </span>
                        )}
                      </div>
                      {c.tag_name !== c.github_name && c.github_name && (
                        <div className="mt-0.5 text-sm text-text">{c.github_name}</div>
                      )}
                    </div>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>

        {err && <p className="text-sm text-destructive">{err}</p>}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            loading={addBuilds.isPending}
            disabled={selected.size === 0}
          >
            {addBuilds.isPending
              ? "Merging…"
              : `Merge ${selected.size || ""} build${selected.size === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const inputCls =
  "w-full rounded-md border border-border-hover bg-bg px-3 py-1.5 text-sm focus:border-accent focus:outline-none";

function Field({
  label,
  hint,
  info,
  right,
  children,
}: {
  label: string;
  /** Small explanatory text rendered *under* the input. */
  hint?: string;
  /** Longer field-purpose description shown on the label's "i" hover. */
  info?: React.ReactNode;
  right?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <label className="flex items-center text-xs font-medium uppercase tracking-wider text-text-tertiary">
          {label}
          {info && <InfoTip>{info}</InfoTip>}
        </label>
        {right}
      </div>
      {children}
      {hint && <p className="text-xs text-text-tertiary">{hint}</p>}
    </div>
  );
}

function errMsg(e: unknown): string {
  if (e instanceof ApiError) return e.detail;
  if (e instanceof Error) return e.message;
  return String(e);
}

/** "Merge in…" picker — folds another release into the one being viewed
 *  (the survivor). Lists every other release with its status; picking one
 *  confirms, then moves its builds + items here and deletes it. */
function MergeControl({
  survivor,
  onDone,
}: {
  survivor: Release;
  onDone?: () => void;
}) {
  const releases = useReleases();
  const merge = useMergeRelease();
  const confirm = useConfirm();
  const [err, setErr] = useState("");

  const candidates = (releases.data ?? []).filter((r) => r.id !== survivor.id);
  if (candidates.length === 0) return null;

  const onPick = async (absorbId: string) => {
    if (!absorbId) return;
    const target = candidates.find((r) => r.id === absorbId);
    const label = target?.title || "Untitled";
    const ok = await confirm({
      title: "Merge releases?",
      description: (
        <>
          Fold <strong>{label}</strong> ({target?.status}) into{" "}
          <strong>{survivor.title || "this release"}</strong>. Its builds and
          items move here, and <strong>{label}</strong> is deleted. This can't
          be undone.
        </>
      ),
      confirmLabel: "Merge",
    });
    if (!ok) return;
    setErr("");
    try {
      await merge.mutateAsync({ survivorId: survivor.id, absorbId });
      onDone?.();
    } catch (e) {
      setErr(errMsg(e));
    }
  };

  return (
    <div className="px-1 py-0.5">
      <select
        className="w-full rounded border border-border-hover bg-card px-2 py-1.5 text-sm text-text-secondary hover:border-text/40 hover:text-text disabled:opacity-60"
        value=""
        disabled={merge.isPending}
        onChange={(e) => onPick(e.target.value)}
        title="Fold another release into this one"
      >
        <option value="">{merge.isPending ? "Merging…" : "Merge in…"}</option>
        {candidates.map((r) => (
          <option key={r.id} value={r.id}>
            {(r.title || "Untitled") + " · " + r.status}
          </option>
        ))}
      </select>
      {err && <span className="mt-1 block text-xs text-destructive">{err}</span>}
    </div>
  );
}

/** Overflow "More" popover for secondary/structural/destructive actions.
 *  Same click-outside idiom as FlagPicker. Children get a `close` callback so
 *  each item can dismiss the menu after acting. */
function MoreMenu({ children }: { children: (close: () => void) => ReactNode }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("pointerdown", onDown);
    return () => window.removeEventListener("pointerdown", onDown);
  }, [open]);
  return (
    <div ref={ref} className="relative">
      <Button
        variant="ghost"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        title="More actions"
      >
        More ▾
      </Button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 z-20 mt-1 w-60 rounded-md border border-border bg-card p-1 shadow-lg"
        >
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  );
}

function MenuItem({
  onClick,
  disabled,
  destructive,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  destructive?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "flex w-full items-center rounded px-2.5 py-1.5 text-left text-sm disabled:opacity-50",
        destructive
          ? "text-destructive hover:bg-destructive/10"
          : "text-text-secondary hover:bg-border/60 hover:text-text",
      )}
    >
      {children}
    </button>
  );
}

// ─── Items — grouped, read-mode rows that expand to the editor ───────────

const CATEGORY_ORDER: ItemCategory[] = [
  "feature",
  "enhancement",
  "bugfix",
  "maintenance",
];
const CATEGORY_LABEL: Record<ItemCategory, string> = {
  feature: "Features",
  enhancement: "Enhancements",
  bugfix: "Bug fixes",
  maintenance: "Maintenance",
};
const CATEGORY_DOT: Record<ItemCategory, string> = {
  feature: "bg-accent",
  enhancement: "bg-nightly",
  bugfix: "bg-ea",
  maintenance: "bg-text-tertiary",
};

function ItemGroups({
  items,
  releaseId,
  readOnly = false,
}: {
  items: ReleaseItem[];
  releaseId: string;
  readOnly?: boolean;
}) {
  const known = new Set<string>(CATEGORY_ORDER);
  const groups = CATEGORY_ORDER.map((cat) => ({
    cat,
    label: CATEGORY_LABEL[cat],
    list: items.filter((i) => i.category === cat),
  })).filter((g) => g.list.length > 0);
  const others = items.filter((i) => !known.has(i.category));

  return (
    <div className="space-y-4">
      {groups.map((g) => (
        <ItemGroup
          key={g.cat}
          category={g.cat}
          label={g.label}
          list={g.list}
          releaseId={releaseId}
          total={items.length}
          readOnly={readOnly}
        />
      ))}
      {others.length > 0 && (
        <ItemGroup
          key="other"
          label="Other"
          list={others}
          releaseId={releaseId}
          total={items.length}
          readOnly={readOnly}
        />
      )}
    </div>
  );
}

function ItemGroup({
  category,
  label,
  list,
  releaseId,
  total,
  readOnly = false,
}: {
  category?: ItemCategory;
  label: string;
  list: ReleaseItem[];
  releaseId: string;
  total: number;
  readOnly?: boolean;
}) {
  // A group with a blank (just-added) row always shows so the editor can fill
  // it in; otherwise default open for small releases, collapsed for big ones.
  const hasBlank = list.some((i) => i.text.trim() === "");
  const [userOpen, setUserOpen] = useState<boolean | null>(null);
  const open = hasBlank || (userOpen ?? total <= 12);
  return (
    <div>
      <button
        type="button"
        onClick={() => setUserOpen(!open)}
        className="flex w-full items-center gap-2 text-left"
      >
        <span className="text-[11px] text-text-tertiary">{open ? "▾" : "▸"}</span>
        {category && (
          <span
            className={cn("h-1.5 w-1.5 rounded-full", CATEGORY_DOT[category])}
            aria-hidden
          />
        )}
        <span className="text-xs font-semibold uppercase tracking-wider text-text-tertiary">
          {label}
        </span>
        <span className="rounded-full bg-border px-1.5 text-[10px] font-medium text-text-secondary">
          {list.length}
        </span>
      </button>
      {open && (
        <ul className="mt-2 space-y-1.5">
          {list.map((it) => (
            <li key={it.id}>
              <ItemRow item={it} releaseId={releaseId} readOnly={readOnly} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ItemRow({
  item,
  releaseId,
  readOnly = false,
}: {
  item: ReleaseItem;
  releaseId: string;
  readOnly?: boolean;
}) {
  // New blank rows open straight into the editor — but never in read-only mode.
  const [editing, setEditing] = useState(!readOnly && item.text.trim() === "");
  if (editing) {
    return (
      <ItemEditor
        item={item}
        releaseId={releaseId}
        onDone={() => setEditing(false)}
      />
    );
  }
  const flagCount = item.linked_flag_ids.length;
  const ticketCount = item.linear_ticket_ids.length;
  const override =
    item.rollout_override === "ea" || item.rollout_override === "ga"
      ? item.rollout_override.toUpperCase()
      : null;
  const meta = (
    <span className="flex shrink-0 items-center gap-1.5 pt-0.5 text-[10px] text-text-tertiary">
      {override && (
        <span className="rounded bg-border px-1 font-semibold uppercase tracking-wider">
          {override}
        </span>
      )}
      {flagCount > 0 && <span title="Linked flags">⚑{flagCount}</span>}
      {ticketCount > 0 && <span title="Linear tickets">#{ticketCount}</span>}
      {!readOnly && <span className="opacity-0 group-hover:opacity-100">Edit</span>}
    </span>
  );
  // Read-only mode (Content Author): a static row, not a click-to-edit button.
  if (readOnly) {
    return (
      <div className="flex w-full items-start gap-2 px-2 py-1.5">
        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-border" aria-hidden />
        <span className="min-w-0 flex-1 text-sm text-text">
          {item.text.trim() || (
            <span className="italic text-text-tertiary">(empty)</span>
          )}
        </span>
        {meta}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className="group flex w-full items-start gap-2 rounded-md border border-transparent px-2 py-1.5 text-left hover:border-border-hover hover:bg-bg/50"
    >
      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-border" aria-hidden />
      <span className="min-w-0 flex-1 text-sm text-text">
        {item.text.trim() || (
          <span className="italic text-text-tertiary">(empty — click to edit)</span>
        )}
      </span>
      {meta}
    </button>
  );
}

/** "+ Add item" modal — pick the category (and optionally type the text)
 *  before the row is created, so it drops straight into the right group. */
function AddItemModal({
  open,
  onOpenChange,
  releaseId,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  releaseId: string;
}) {
  const addItem = useAddItem();
  const [category, setCategory] = useState<ItemCategory>("feature");
  const [text, setText] = useState("");
  const [override, setOverride] = useState<"auto" | "ea" | "ga">("auto");
  const [parity, setParity] = useState("");
  const [tickets, setTickets] = useState("");
  const [flags, setFlags] = useState<string[]>([]);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) {
      setCategory("feature");
      setText("");
      setOverride("auto");
      setParity("");
      setTickets("");
      setFlags([]);
      setErr("");
    }
  }, [open]);

  const submit = async () => {
    setErr("");
    try {
      await addItem.mutateAsync({
        releaseId,
        body: {
          category,
          // Send the trimmed value (empty string, not null) so a blank field
          // means blank — null makes the server fall back to a surface default.
          text: text.trim(),
          parity_notes: parity.trim(),
          linked_flag_ids: flags,
          linear_ticket_ids: tickets.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean),
          rollout_override: override === "auto" ? null : override,
        },
      });
      onOpenChange(false);
    } catch (e) {
      setErr(errMsg(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add item</DialogTitle>
          <DialogDescription>
            Fill in the item, then it drops into the matching category group.
            Everything here is editable on the row afterward too.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-text-tertiary">
              Category
            </label>
            <div className="grid grid-cols-2 gap-2">
              {CATEGORY_ORDER.map((cat) => {
                const selected = category === cat;
                return (
                  <button
                    key={cat}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => setCategory(cat)}
                    className={cn(
                      "flex items-center gap-2 rounded-md border px-3 py-2 text-left text-sm transition-colors",
                      selected
                        ? "border-accent bg-accent/10 text-text ring-1 ring-accent"
                        : "border-border text-text-secondary hover:border-border-hover hover:bg-border/30 hover:text-text",
                    )}
                  >
                    <span
                      className={cn("h-2 w-2 shrink-0 rounded-full", CATEGORY_DOT[cat])}
                      aria-hidden
                    />
                    <span className="flex-1">{CATEGORY_LABEL[cat]}</span>
                    {selected && <span className="text-accent" aria-hidden>✓</span>}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-text-tertiary">
              Description
            </label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={2}
              autoFocus
              placeholder="One short sentence, present tense, user-oriented."
              className={inputCls}
            />
          </div>

          <div>
            <label className="mb-1.5 flex items-center text-xs font-medium uppercase tracking-wider text-text-tertiary">
              Rollout
              <InfoTip>
                Auto follows the linked flag(s), or GA if none. Force EA/GA to
                override regardless of flag state.
              </InfoTip>
            </label>
            <div className="inline-flex rounded-md border border-border-hover bg-bg p-0.5">
              {(
                [
                  ["auto", "Auto"],
                  ["ga", "GA"],
                  ["ea", "EA"],
                ] as ["auto" | "ga" | "ea", string][]
              ).map(([v, label]) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setOverride(v)}
                  className={
                    override === v
                      ? "rounded-sm bg-card px-3 py-1 text-xs font-medium text-text shadow-sm"
                      : "rounded-sm px-3 py-1 text-xs text-text-secondary hover:text-text"
                  }
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-text-tertiary">
                Parity notes
              </label>
              <input
                value={parity}
                onChange={(e) => setParity(e.target.value)}
                placeholder="e.g. iOS 16+ only"
                className={inputCls}
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-text-tertiary">
                Linear tickets
              </label>
              <input
                value={tickets}
                onChange={(e) => setTickets(e.target.value)}
                placeholder="ENG-1234, ENG-5678"
                className={cn(inputCls, "font-mono")}
              />
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-text-tertiary">
              Linked flags
            </label>
            <FlagPicker value={flags} onChange={setFlags} />
          </div>

          {err && <p className="text-sm text-destructive">{err}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} loading={addItem.isPending}>
            Add item
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
